import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Generic error boundary that prevents a single component crash from breaking the entire app.
 * Use sparingly — wrap risky/data-heavy screens (e.g., Wallet, Performance).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.name || 'component', 'crashed:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary-fallback" className="bg-white rounded-xl border border-red-200 p-8 text-center">
          <AlertCircle size={36} className="text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-1">Something went wrong</h2>
          <p className="text-sm text-gray-500 mb-4">
            We hit an unexpected error loading this section. Try refreshing — your data is safe.
          </p>
          <button
            onClick={this.handleReset}
            data-testid="error-boundary-retry"
            className="inline-flex items-center gap-2 bg-[#37b34a] hover:bg-[#2d9a3e] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw size={14} /> Try again
          </button>
          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <pre className="mt-4 text-left text-xs text-red-700 bg-red-50 rounded p-3 overflow-auto max-h-48">
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
